import { HttpRequest } from "./HttpRequest.js";
import { Logger } from "../core/Logger.js";

/**
 * The Persistable class connects to a scene element and manages persistent data storage associated with that element.
 */
export class Persistable {
  static _env = "dev";
  static _scene;
  static _apiUrl;
  static _type = "Persistable";
  static _api_key = "";

  constructor(uuid, networkable) {
    this._uuid = uuid;
    this._networkable = networkable;
    this._lockId = null;
  }

  /**
   * Sets the environment and API URL that will be used by all Persistable API calls. (internal)
   * @param {object} scene The scene reference.
   * @param {string} env The environment used for managing storage scopes (e.g., 'dev', 'prod').
   * @param {string} apiUrl The base URL of the Persistable backend API.
   * @param {string} apiKey The API key to use for authentication.
   */
  static setup(scene, env, apiUrl, apiKey) {
    if (!apiUrl || typeof apiUrl !== "string") {
      throw new Error(
        "Persistable.setup: apiUrl is required and must be a string."
      );
    }

    Persistable._env = env || "dev";
    Persistable._scene = scene;
    Persistable._apiUrl = apiUrl;
    Persistable._api_key = apiKey;
  }

  /**
   * Returns a Persistable instance, connected to a specific object in the scene for persistence operations.
   * @param {string} objectName The name or ID of the object to associate with this persistable store.
   */
  static use(objectName) {
    if (!Persistable._scene) {
      throw new Error(
        "Persistable.use: Persistable has not been set up with a scene. Call Persistable.setup() first."
      );
    }
    const target = Persistable._scene.getObjectByName(objectName);

    if (!target) {
      throw new Error(
        `Persistable.use: Object with name "${objectName}" not found in the scene.`
      );
    }

    if (target.type !== Persistable._type) {
      Logger.warn(`Object ${objectName} is not explicitly typed as 'Persistable'. Ensure it has a valid UUID.`);
      throw new Error(`Object ${objectName} is not a Persistable object.`);
    }

    if (!target.uuid) {
      throw new Error(
        `Persistable.use: Object "${objectName}" does not have a UUID.`
      );
    }

    return new Persistable(target.uuid, target.networkable || false);
  }

  async _sendCommand(command, params = {}, requiresLock = false) {
    if (!Persistable._apiUrl) {
      const errMsg =
        "Persistable API URL is not configured. Call Persistable.setup().";
      Logger.error(errMsg);
      throw new Error(errMsg);
    }
    if (!this._uuid) {
      const errMsg = "Persistable UUID is not available for this instance.";
      Logger.error(errMsg);
      throw new Error(errMsg);
    }

    const url = `${Persistable._apiUrl}/persistable/${this._uuid}`;
    const requestBody = {
      cmd: command,
      params: { ...params },
      env: Persistable._env,
    };

    if (requiresLock && this._lockId) {
      requestBody.params.lockId = this._lockId;
    }

    try {
      const rawHttpResponse = await HttpRequest.post(url, {
        json: requestBody,
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": Persistable._api_key,
        },
      });

      const parsedJsonResponse = await rawHttpResponse.json();

      if (parsedJsonResponse && parsedJsonResponse.success === true) {
        return parsedJsonResponse.data;
      } else {
        const errorMessage =
          parsedJsonResponse && parsedJsonResponse.data && parsedJsonResponse.data.error
            ? parsedJsonResponse.data.error
            : `Command '${command}' failed as reported by server (application-level success: false).`;
        Logger.error(
          `Persistable command '${command}' (application-level failure for UUID '${this._uuid}'): ${errorMessage}`,
          parsedJsonResponse
        );
        throw new Error(errorMessage);
      }
    } catch (error) {
      let errorMessage = error.message || `Command '${command}' failed due to an unknown error.`;

      if (error.name === 'HTTPError' && error.response) {
        try {
          const errorBody = await error.response.json();
          if (errorBody && errorBody.data && errorBody.data.error) {
            errorMessage = errorBody.data.error;
          } else if (errorBody && errorBody.error) {
             errorMessage = errorBody.error;
          } else {
            errorMessage = `HTTP Error ${error.response.status}: ${error.response.statusText}. ` + (errorBody ? `Details: ${JSON.stringify(errorBody)}` : 'No further details in JSON body.');
          }
        } catch (jsonParseError) {
          errorMessage = `HTTP Error ${error.response.status}: ${error.response.statusText}. (Failed to parse error response body: ${jsonParseError.message})`;
        }
      } else if (error instanceof SyntaxError && error.message.toLowerCase().includes("json")) {
        errorMessage = `Failed to parse server response as JSON for command '${command}'. Original error: ${error.message}`;
      }

      Logger.error(errorMessage);
      throw new Error(errorMessage);
    }
  }

  /**
   * Returns the current state of all persisted properties.
   * @returns {Promise<object|null>} A promise that resolves to the object's data, or null on failure.
   */
  async inspect() {
    try {
      return await this._sendCommand("inspect");
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetches a value from the persistent storage by its key.
   * @param {string} key The name of the property to retrieve.
   * @param {any} [defaultValue=undefined] The default value to return if the property is not found or an error occurs.
   * @returns {Promise<any>} A promise that resolves to the property's value, or the defaultValue.
   */
  async get(key, defaultValue = undefined) {
    if (!key || typeof key !== "string") {
      Logger.error("Persistable.get: key must be a non-empty string.");
      return defaultValue;
    }

    try {
      const responseData = await this._sendCommand("get", { prop: key });
      return responseData.value !== undefined || responseData.value !== null
        ? responseData.value
        : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  }

  /**
   * Stores a value under the given key, replacing any existing value.
   * Requires a lock if the object is locked.
   * @param {string} key The name of the property to set.
   * @param {any} value The value to store.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async set(key, value) {
    if (!key || typeof key !== "string") {
      throw new Error("Persistable.set: key must be a non-empty string.");
    }
    return this._sendCommand("set", { prop: key, value: value }, true);
  }

  /**
   * Deletes a specific key-value pair from the persistent storage.
   * Requires a lock if the object is locked.
   * @param {string} key The name of the property to remove.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async delete(key) {
    if (!key || typeof key !== "string") {
      throw new Error("Persistable.delete: key must be a non-empty string.");
    }
    return this._sendCommand("delete", { prop: key }, true);
  }

  /**
   * Removes all stored data associated with the connected object, resetting it to an empty object.
   * Requires a lock if the object is locked.
   * @returns {Promise<boolean>} A promise that resolves to true on success, false on failure.
   */
  async clear() {
    try {
      await this._sendCommand("clear", {}, true);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Increments a numeric stored value by a specified amount.
   * Requires a lock if the object is locked.
   * @param {string} key The key of a numeric value to increment.
   * @param {number} value The amount to add.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async inc(key, value) {
    if (!key || typeof key !== "string" || typeof value !== "number") {
      throw new Error("Persistable.inc: invalid parameters.");
    }
    return this._sendCommand("inc", { prop: key, value: value }, true);
  }

  /**
   * Multiplies a numeric stored value by the given factor.
   * Requires a lock if the object is locked.
   * @param {string} key The key of a numeric value to multiply.
   * @param {number} value The multiplier.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async mult(key, value) {
    if (!key || typeof key !== "string" || typeof value !== "number") {
      throw new Error("Persistable.mult: invalid parameters.");
    }
    return this._sendCommand("mult", { prop: key, value: value }, true);
  }

  /**
   * Pushes an item to the end of an array in the persistent storage.
   * Requires a lock if the object is locked.
   * @param {string} key The name of the array property.
   * @param {any} item The item to append to the array.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async arrPush(key, item) {
    if (!key || typeof key !== "string") {
      throw new Error("Persistable.arrPush: key must be a non-empty string.");
    }
    return this._sendCommand("arrPush", { prop: key, value: item }, true);
  }

  /**
   * Inserts an item at a specific position in an array stored persistently.
   * Requires a lock if the object is locked.
   * @param {string} key The name of the array property.
   * @param {number} index The position at which to insert the item.
   * @param {any} item The item to insert.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async arrInsertAt(key, index, item) {
    if (!key || typeof key !== "string" || typeof index !== "number") {
      throw new Error("Persistable.arrInsertAt: invalid parameters.");
    }
    return this._sendCommand(
      "arrInsertAt",
      { prop: key, index: index, value: item },
      true
    );
  }

  /**
   * Removes all instances of a value from an array in persistent storage.
   * Requires a lock if the object is locked.
   * @param {string} key The name of the array property.
   * @param {any} value The value to remove from the array.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async arrPull(key, value) {
    if (!key || typeof key !== "string") {
      throw new Error("Persistable.arrPull: key must be a non-empty string.");
    }
    return this._sendCommand("arrPull", { prop: key, value: value }, true);
  }

  /**
   * Removes an item at a specific index from an array stored persistently.
   * Requires a lock if the object is locked.
   * @param {string} key The name of the array property.
   * @param {number} index The position of the element to remove.
   * @returns {Promise<object|null>} A promise that resolves to the updated object data, or null on failure.
   */
  async arrPullAt(key, index) {
    if (!key || typeof key !== "string" || typeof index !== "number") {
      throw new Error("Persistable.arrPullAt: invalid parameters.");
    }
    return this._sendCommand("arrPullAt", { prop: key, index: index }, true);
  }

  /**
   * Locks the persistable object to prevent other operations for a given timeout duration.
   * @param {number} timeoutInMilliseconds Duration in seconds to maintain the lock.
   * @returns {Promise<boolean>} A promise that resolves to true on success, false on failure.
   */
  async lock(timeoutInMilliseconds) {
    if (
      typeof timeoutInMilliseconds !== "number" ||
      timeoutInMilliseconds <= 0
    ) {
      throw new Error(
        "Persistable.lock: timeout must be a positive number (seconds)."
      );
    }
    try {
      const responseData = await this._sendCommand(
        "lock",
        { timeout: timeoutInMilliseconds },
        false
      );
      if (responseData && responseData.lockId) {
        this._lockId = responseData.lockId;
        return /*responseData*/ true;
      }

      return false;
    } catch (error) {
      this._lockId = null;
      throw error;
    }
  }

  /**
   * Manually unlocks the persistable object, enabling further operations.
   * This action is only effective if the object is currently locked by this instance's lockId.
   * @returns {Promise<boolean>} A promise that resolves to true on successful unlock, false otherwise.
   */
  async unlock() {
    if (!this._lockId) {
      throw new Error(
        "Cannot unlock: object is not currently locked by this instance."
      );
    }
    try {
      await this._sendCommand("unlock", { lockId: this._lockId }, false);
      this._lockId = null;
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Sets a time-to-live (TTL) for the stored data. The data will expire after the given timestamp.
   * Requires a lock if the object is locked.
   * @param {number} timestampInMilliseconds A Unix timestamp (milliseconds) after which the data should expire.
   * @returns {Promise<boolean>} A promise that resolves to true on success, false on failure.
   */
  async ttl(timestampInMilliseconds) {
    if (typeof timestampInMilliseconds !== "number" || timestampInMilliseconds <= 0) {
      throw new Error(
        "Persistable.ttl: timestamp must be a positive integer Unix timestamp (seconds)."
      );
    }
    try {
      await this._sendCommand("ttl", { timestamp: timestampInMilliseconds }, true);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Drops (deletes) the entire persistable object from the storage.
   * Requires a lock if the object is locked.
   * @returns {Promise<boolean>} A promise that resolves to true if the object was dropped successfully, false otherwise.
   */
  async drop() {
    try {
      await this._sendCommand("drop", {}, true);
      return true;
    } catch (error) {
      return false;
    }
  }
}
